<template>
    <template v-if="(tooltipText ?? '').length > 0 || $slots.content">
        <!-- We need to explicitly pass the class to Tippy otherwise custom classes are not applied -->
        <Tippy
            allowHtml
            animation="shift-toward"
            :appendTo
            :class="`inline-flex cursor-pointer ${$attrs.class ?? ''}`"
            :delay="[openDelay, closeDelay]"
            :hideOnClick="hideTooltipOnClick"
            :offset="offset && [offset.skidding, offset.distance]"
            :placement="tooltipLocation"
            theme="tooltip-theme"
            :trigger
        >
            <template #content>
                <template v-if="$slots.content">
                    <slot name="content" />
                </template>
                <template v-else>
                    <div>{{ tooltipText }}</div>
                </template>
            </template>
            <slot v-bind="$attrs" />
        </Tippy>
    </template>
    <template v-else>
        <slot v-bind="$attrs" />
    </template>
</template>

<script setup lang="ts">
import type { DsTooltipProps } from '@/components/design-system/tooltip/tooltip.types';
import { Tippy } from 'vue-tippy';

withDefaults(defineProps<DsTooltipProps>(), {
    tooltipLocation: 'top',
    openDelay: 250,
    closeDelay: 100,
    hideTooltipOnClick: true,
    trigger: 'mouseenter focusin',
    appendTo: 'parent',
});

defineOptions({
    inheritAttrs: false,
});
</script>

<style lang="scss">
@import 'tippy.js/animations/shift-toward.css';
@import 'tippy.js/dist/tippy.css';

.tippy-box[data-theme~='tooltip-theme'] {
    @apply bg-ds-primary-900 text-xs text-content-inverse;
}

.tippy-box[data-theme~='tooltip-theme'][data-placement^='top'] > .tippy-arrow::before {
    @apply border-t-ds-primary-900;
}
.tippy-box[data-theme~='tooltip-theme'][data-placement^='bottom'] > .tippy-arrow::before {
    @apply border-b-ds-primary-900;
}
.tippy-box[data-theme~='tooltip-theme'][data-placement^='left'] > .tippy-arrow::before {
    @apply border-l-ds-primary-900;
}
.tippy-box[data-theme~='tooltip-theme'][data-placement^='right'] > .tippy-arrow::before {
    @apply border-r-ds-primary-900;
}

.tippy-box[data-theme~='tooltip-theme'] > .tippy-arrow::before {
    @apply scale-90 transform;
}
</style>
